import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft } from 'lucide-react';
import AsyncSubmitButton from '../ui/AsyncSubmitButton';
import { INQUIRY_STATUSES, labelFor, receptionApiErrorMessage } from './receptionConstants';
import ReceptionActivityTimeline from './ReceptionActivityTimeline';
import ReceptionConvertDialog from './ReceptionConvertDialog';

export default function ReceptionInquiryDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const { canManageFeature, hasPermission } = useAuth();
  const canManage = canManageFeature('reception') || hasPermission('manage_reception');
  const canConvert = hasPermission('convert_reception_inquiry') || hasPermission('manage_students');

  const [inquiry, setInquiry] = useState(null);
  const [history, setHistory] = useState({ followups: [], activities: [] });
  const [followup, setFollowup] = useState({ remarks: '', next_followup_date: '', status: '' });
  const [convertOpen, setConvertOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [detail, hist] = await Promise.all([
        api.get(`/reception/inquiries/${id}`),
        api.get(`/reception/inquiries/${id}/history`),
      ]);
      setInquiry(detail.data?.data);
      setHistory(hist.data?.data || { followups: [], activities: [] });
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const addFollowup = async ({ beginLoading }) => {
    beginLoading();
    try {
      await api.post(`/reception/inquiries/${id}/followups`, {
        followup_date: todayString(),
        ...followup,
      });
      toast({ title: 'Follow-up saved' });
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
      throw e;
    }
  };

  const handleFollowupSuccess = () => {
    setFollowup({ remarks: '', next_followup_date: '', status: '' });
    load();
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!inquiry) return <div className="p-8 text-center">Inquiry not found</div>;

  const timelineItems = [
    ...(history.followups || []).map((f) => ({ ...f, action: `Follow-up (${f.status || 'note'})`, details: f.remarks })),
    ...(history.activities || []),
  ].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild><Link to="/reception/inquiries"><ArrowLeft className="w-4 h-4" /></Link></Button>
        <div>
          <h1 className="text-2xl font-bold">{inquiry.student_name}</h1>
          <p className="text-muted-foreground">{labelFor(INQUIRY_STATUSES, inquiry.status)} · {inquiry.class_name || 'No class'}</p>
        </div>
        {canConvert && inquiry.status !== 'admission_confirmed' && (
          <Button className="ml-auto" onClick={() => setConvertOpen(true)}>Convert to Admission</Button>
        )}
        {inquiry.converted_student_id && (
          <Button variant="outline" className="ml-auto" asChild>
            <Link to={`/students/${inquiry.converted_student_id}`}>View Student</Link>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Inquiry Details</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Parent:</span> {inquiry.parent_name || inquiry.father_name || '—'}</p>
            <p><span className="text-muted-foreground">Mobile:</span> {inquiry.mobile_number || '—'}</p>
            <p><span className="text-muted-foreground">Email:</span> {inquiry.email || '—'}</p>
            <p><span className="text-muted-foreground">City:</span> {inquiry.city || '—'}</p>
            <p><span className="text-muted-foreground">Previous School:</span> {inquiry.previous_school || '—'}</p>
            <p><span className="text-muted-foreground">Inquiry Date:</span> {inquiry.inquiry_date || '—'}</p>
            <p><span className="text-muted-foreground">Next Follow-up:</span> {inquiry.next_followup_date || '—'}</p>
            <p><span className="text-muted-foreground">Counselor:</span> {inquiry.assigned_to_name || '—'}</p>
            <p><span className="text-muted-foreground">Remarks:</span> {inquiry.remarks || '—'}</p>
          </CardContent>
        </Card>

        {canManage && (
          <Card>
            <CardHeader><CardTitle>Add Follow-up</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label>Remarks</Label><Input value={followup.remarks} onChange={(e) => setFollowup({ ...followup, remarks: e.target.value })} /></div>
              <div><Label>Next Follow-up Date</Label><DateInput value={followup.next_followup_date} onChange={(v) => setFollowup({ ...followup, next_followup_date: v })} /></div>
              <div><Label>Update Status</Label>
                <Select value={followup.status || 'none'} onValueChange={(v) => setFollowup({ ...followup, status: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {INQUIRY_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <AsyncSubmitButton
                onAction={addFollowup}
                onSuccess={handleFollowupSuccess}
                loadingLabel="Saving…"
              >
                Save Follow-up
              </AsyncSubmitButton>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>History & Timeline</CardTitle></CardHeader>
        <CardContent><ReceptionActivityTimeline items={timelineItems} /></CardContent>
      </Card>

      {convertOpen && (
        <ReceptionConvertDialog
          inquiryId={inquiry.id}
          defaultClassId={inquiry.interested_class_id}
          onClose={() => setConvertOpen(false)}
          onSuccess={() => { setConvertOpen(false); load(); }}
        />
      )}
    </div>
  );
}
